import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { mergeServerProgress, readProgress, markJourneyStepComplete, completeJourneyStep } from '../utils/journeyProgress';
import { getJourneyConfig } from '../config/onboardingCatalog';

const UserJourney = ({
  open,
  onClose,
  onOpenTeamManager,
  onOpenSettings,
  experienceMode = 'full',
  terms = {},
  serverSteps = {},
}) => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [liveAnnouncement, setLiveAnnouncement] = useState('');
  const prevProgressRef = useRef({});
  const panelRef = useRef(null);
  const returnFocusRef = useRef(null);
  const reducedMotion = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const journeyConfig = getJourneyConfig({ experienceMode, terms });
  const journeySteps = journeyConfig.steps;

  useEffect(() => {
    var handler = function () { setIsMobile(window.innerWidth < 768); };
    window.addEventListener('resize', handler);
    return function () { window.removeEventListener('resize', handler); };
  }, []);

  // Refresh progress each time the modal opens.
  // IMPORTANT: do NOT call mergeServerProgress(serverSteps) here — that replaces
  // localStorage with a stale login-time snapshot, wiping steps the user just completed.
  // Instead take the union: step is done if localStorage OR serverSteps says so.
  useEffect(() => {
    if (open) {
      const local = readProgress();
      const serverComplete = Object.keys(serverSteps || {})
        .filter(k => Boolean(serverSteps[k]))
        .reduce((acc, k) => ({ ...acc, [k]: true }), {});
      setProgress({ ...serverComplete, ...local });
      returnFocusRef.current = document.activeElement;
      window.requestAnimationFrame(() => panelRef.current?.focus());
      return function () {
        if (returnFocusRef.current?.focus) returnFocusRef.current.focus();
      };
    }
  }, [open, serverSteps]);

  useEffect(() => {
    const refresh = () => {
      const next = readProgress();
      // Detect newly completed steps since last render
      const journeyConfig = getJourneyConfig({ experienceMode, terms });
      const newlyDone = journeyConfig.steps.filter(
        s => next[s.id] && !prevProgressRef.current[s.id]
      );
      if (newlyDone.length > 0) {
        const totalDone = journeyConfig.steps.filter(s => next[s.id]).length;
        const lastName = newlyDone[newlyDone.length - 1].title || newlyDone[newlyDone.length - 1].id;
        setLiveAnnouncement(`Step complete: ${lastName}. ${totalDone} of ${journeyConfig.steps.length} done.`);
      }
      prevProgressRef.current = next;
      setProgress(next);
    };
    window.addEventListener('journey-progress-updated', refresh);
    return () => window.removeEventListener('journey-progress-updated', refresh);
  }, [experienceMode, terms]);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return function () { document.body.style.overflow = ''; };
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll(
        'button:not([disabled]), [href], summary, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) {
        event.preventDefault();
        panelRef.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  var completedCount = journeySteps.filter(function (s) { return !!progress[s.id]; }).length;
  // O(n) "next incomplete" — computed once per render.
  var firstIncompleteId = (journeySteps.find(function (s) { return !progress[s.id]; }) || {}).id;

  var handleStart = function (step) {
    onClose();
    if (step.action === 'openSettings') {
      if (onOpenSettings) onOpenSettings();
    } else if (step.action === 'teamManager' || step.action === 'teamManagerReports') {
      if (onOpenTeamManager) onOpenTeamManager();
    } else if (step.path) {
      navigate(step.path);
    }
  };

  var handleMarkDone = function (step) {
    markJourneyStepComplete(step.id); // immediate localStorage write + dispatches event
    setProgress(readProgress());      // refresh UI right away
    completeJourneyStep(step.id);     // server sync in background (handles retry queue)
  };

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="user-journey-title"
      tabIndex={-1}
      style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000,
      display: 'flex', justifyContent: 'center', alignItems: isMobile ? 'stretch' : 'flex-start',
    }}>
      {/* Screen reader live region — announces milestone completions */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}
      >
        {liveAnnouncement}
      </div>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.65)' }}
      />

      {/* Panel */}
      <div style={{
        position: 'relative',
        width: '100%',
        maxWidth: isMobile ? '100%' : '520px',
        height: isMobile ? '100%' : 'auto',
        maxHeight: isMobile ? '100%' : '90vh',
        marginTop: isMobile ? 0 : '40px',
        background: 'var(--bg-card, #15151F)',
        borderRadius: isMobile ? 0 : '16px',
        border: isMobile ? 'none' : '1px solid var(--border-color, #1E1E2B)',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      }}>
        {/* Sticky header */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 1,
          background: 'var(--bg-card, #15151F)',
          padding: '20px 24px 16px',
          borderBottom: '1px solid var(--border-color, #1E1E2B)',
        }}>
          {/* Close button */}
          <button
            onClick={onClose}
            style={{
              position: 'absolute', top: 16, right: 16,
              background: 'none', border: 'none',
              color: 'var(--text-dim, #9A9AB2)',
              fontSize: '20px', cursor: 'pointer',
              padding: '4px 8px', lineHeight: 1,
            }}
            aria-label="Close"
          >
            ✕
          </button>

          <div style={{
            color: 'var(--text-dim, #9A9AB2)', fontSize: '11px',
            textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px',
          }}>
            Your Coaching Journey
          </div>
          <h2 id="user-journey-title" style={{
            color: 'var(--text-primary, #fff)', fontSize: '20px',
            fontWeight: 700, margin: 0,
          }}>
            <span aria-hidden="true">{'\uD83D\uDCCD'} </span>{journeyConfig.title}
          </h2>

          {/* Progress bar */}
          <div style={{ marginTop: '16px' }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: '6px',
            }}>
              <span style={{ color: 'var(--text-secondary, #495057)', fontSize: '12px' }}>
                {completedCount} of {journeySteps.length} completed
              </span>
              <span style={{
                color: completedCount === journeySteps.length ? 'var(--accent)' : 'var(--accent)',
                fontSize: '12px', fontWeight: 600,
              }}>
                {Math.round((completedCount / journeySteps.length) * 100)}%
              </span>
            </div>
            <div
              role="progressbar"
              aria-label="Journey progress"
              aria-valuemin={0}
              aria-valuemax={journeySteps.length}
              aria-valuenow={completedCount}
              style={{
              height: '4px', borderRadius: '2px',
              background: 'var(--bg-input, #f1f3f5)',
              overflow: 'hidden',
            }}>
              <div style={{
                height: '100%', borderRadius: '2px',
                background: completedCount === journeySteps.length ? 'var(--accent)' : 'var(--accent)',
                width: (completedCount / journeySteps.length) * 100 + '%',
                transition: reducedMotion ? 'none' : 'width 0.5s ease',
              }} />
            </div>
          </div>
        </div>

        {/* Steps */}
        <div role="list" style={{ padding: '12px 24px 24px', flex: 1 }}>
          {journeySteps.map(function (step, index) {
            var isCompleted = !!progress[step.id];
            var isNext = !isCompleted && step.id === firstIncompleteId;
            var displayNumber = index + 1;
            var previousStage = index > 0 ? journeySteps[index - 1].stage : null;

            return (
              <React.Fragment key={step.id}>
              {step.stage && step.stage !== previousStage && (
                <h3 style={{
                  color: 'var(--accent)', fontSize: '12px', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.7px',
                  margin: index === 0 ? '2px 0 8px' : '22px 0 8px',
                }}>
                  {step.stage}
                </h3>
              )}
              <div role="listitem" data-testid="journey-step" data-step-id={step.id} style={{
                display: 'flex', gap: '14px', alignItems: 'flex-start',
                padding: '14px 16px',
                marginBottom: '8px',
                borderRadius: '12px',
                background: isNext ? 'var(--bg-input, #f1f3f5)' : 'transparent',
                border: isNext ? '1px solid var(--border-input, #323248)' : '1px solid transparent',
                opacity: isCompleted ? 0.55 : 1,
                transition: reducedMotion ? 'none' : 'all 0.2s ease',
              }}>
                {/* Step number / check */}
                <div style={{
                  minWidth: '32px', height: '32px', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: isCompleted ? '16px' : '13px', fontWeight: 700,
                  background: isCompleted ? '#2DD4BF22' : (isNext ? '#2DD4BF22' : 'var(--bg-input, #f1f3f5)'),
                  color: isCompleted ? 'var(--accent)' : (isNext ? 'var(--accent)' : 'var(--text-dim, #9A9AB2)'),
                  flexShrink: 0, marginTop: '2px',
                }}>
                  {isCompleted ? '\u2705' : displayNumber}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Title row */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    marginBottom: '4px', flexWrap: 'wrap',
                  }}>
                    <span style={{
                      color: isCompleted ? 'var(--text-dim, #9A9AB2)' : 'var(--text-primary, #fff)',
                      fontSize: '15px', fontWeight: 600,
                    }}>
                      {step.title}
                    </span>
                    {step.pro && (
                      <span style={{
                        background: '#f59e0b22', color: '#f59e0b',
                        fontSize: '10px', fontWeight: 700,
                        padding: '2px 6px', borderRadius: '4px',
                        textTransform: 'uppercase', letterSpacing: '0.5px',
                      }}>
                        PRO
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  <p style={{
                    color: 'var(--text-secondary, #495057)', fontSize: '13px',
                    lineHeight: 1.5, margin: '0 0 6px 0',
                  }}>
                    {step.description}
                  </p>

                  {/* Features */}
                  <div style={{
                    color: 'var(--text-dim, #9A9AB2)', fontSize: '11px',
                    marginBottom: '8px',
                  }}>
                    {step.features}
                  </div>

                  {/* Tip / Caveat */}
                  {step.tip && (
                    <div style={{
                      fontSize: '12px',
                      color: step.caveat ? '#f59e0b' : 'var(--text-dim, #9A9AB2)',
                      fontStyle: 'italic', marginBottom: '8px',
                      padding: step.caveat ? '6px 10px' : undefined,
                      background: step.caveat ? '#f59e0b11' : undefined,
                      borderRadius: step.caveat ? '6px' : undefined,
                      borderLeft: step.caveat ? '2px solid #f59e0b' : undefined,
                    }}>
                      {step.caveat ? '\u26A0\uFE0F ' : '\uD83D\uDCA1 '}{step.tip}
                    </div>
                  )}

                  {step.why && (
                    <details style={{
                      marginBottom: '10px', padding: '8px 10px',
                      background: 'var(--bg-input, #f1f3f5)',
                      borderRadius: '8px', color: 'var(--text-secondary, #495057)',
                    }}>
                      <summary
                        aria-label={`Learn the coaching concept: ${step.title}`}
                        style={{ cursor: 'pointer', fontSize: '12px', fontWeight: 700, color: 'var(--accent)' }}
                      >
                        Learn the coaching concept
                      </summary>
                      <div style={{ marginTop: '8px', fontSize: '12px', lineHeight: 1.55 }}>
                        <p style={{ margin: '0 0 6px' }}><strong>Why this matters:</strong> {step.why}</p>
                        <p style={{ margin: '0 0 6px' }}><strong>What good looks like:</strong> {step.goodLooksLike}</p>
                        <p style={{ margin: '0 0 6px' }}><strong>Example:</strong> {step.example}</p>
                        <p style={{ margin: 0 }}><strong>Common mistake:</strong> {step.commonMistake}</p>
                      </div>
                    </details>
                  )}

                  {/* Start / Mark done buttons */}
                  {!isCompleted && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        onClick={function () { handleStart(step); }}
                        aria-label={`Start: ${step.title}`}
                        style={{
                          padding: '6px 16px', borderRadius: '6px', border: 'none',
                          background: isNext ? 'var(--accent)' : 'var(--bg-input, #f1f3f5)',
                          color: isNext ? 'var(--text-on-accent)' : 'var(--text-secondary, #495057)',
                          fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                          transition: reducedMotion ? 'none' : 'all 0.2s',
                        }}
                      >
                        Start <span aria-hidden="true">{'\u2192'}</span>
                      </button>
                      <button
                        onClick={function () { handleMarkDone(step); }}
                        aria-label={`Mark done: ${step.title}`}
                        style={{
                          padding: '6px 14px', borderRadius: '6px',
                          border: '1px solid var(--border-color, #2DD4BF55)',
                          background: 'transparent',
                          color: 'var(--accent)',
                          fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                          transition: reducedMotion ? 'none' : 'all 0.2s',
                        }}
                      >
                        Mark done ✓
                      </button>
                    </div>
                  )}
                </div>
              </div>
              </React.Fragment>
            );
          })}

          {completedCount < journeySteps.length && (
            <p style={{
              textAlign: 'center',
              fontSize: '12px',
              color: 'var(--text-secondary)',
              padding: '12px 0 4px',
            }}>
              Reopen this checklist any time from <strong>Your Journey</strong> in the menu.
            </p>
          )}

          {/* Quote */}
          <div style={{
            marginTop: '24px', padding: '20px',
            borderTop: '1px solid var(--border-color, #1E1E2B)',
            textAlign: 'center',
          }}>
            <p style={{
              color: 'var(--text-dim, #9A9AB2)', fontSize: '12px',
              fontStyle: 'italic', lineHeight: 1.7,
              margin: 0, maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto',
            }}>
              "Your expertise on the pitch. Our intelligence behind it. Together, every session counts."
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserJourney;
